// VinesScene — sweeping climbing-vine arcs across the section.
//
// Visual: long sinuous curves that enter from off-screen corners
// and sweep diagonally across the section, with small offshoot
// leaves along each arc. Different feel from PalmsScene — no
// radiating fronds, no trunk. Reads as "climbing growth,
// exploration."
//
// Used on the Find a competition page, where the user is in
// discovery mode. The scene's geometry intentionally does NOT
// try to cross the seam — instead the beige hero gets one set
// of arcs sweeping toward the seam, and the green section gets
// its own set of arcs continuing the diagonal motion downward.
// The seam reads as a horizon, not a join.

import { Platform, View } from "react-native";
import { COLORS } from "../../../lib/brand";
import Svg, { G, Path } from "react-native-svg";

import type { SceneTone } from "./types";

const CREAM_STROKE = COLORS.LINEN.hex;
const SAGE_STROKE = COLORS.SAGE.hex;

export function VinesScene({ tone }: { tone: SceneTone }) {
  if (Platform.OS !== "web") return null;

  const isBeige = tone === "on-beige";
  const stroke = isBeige ? SAGE_STROKE : CREAM_STROKE;

  // Sage on cream needs more weight; cream on forest stays
  // subtle so it doesn't dominate the dark band.
  const primaryOpacity = isBeige ? 0.55 : 0.16;
  const accentOpacity = isBeige ? 0.4 : 0.12;

  return (
    <View
      pointerEvents="none"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
      }}
    >
      {isBeige ? (
        <>
          {/* Top-left vine: enters from off-screen top-left,
              arcs down and to the right, exits off the right
              edge well above the seam. Establishes the diagonal
              motion. */}
          <Svg
            width={1400}
            height={520}
            viewBox="0 0 1400 520"
            style={{ position: "absolute", top: -60, left: -120, opacity: primaryOpacity }}
          >
            <G stroke={stroke} strokeLinecap="round" fill="none">
              <Path
                d="M-40 40 C 220 130, 520 230, 820 300 C 1080 360, 1280 410, 1400 440"
                strokeWidth={2.4}
              />
              <Path
                d="M-30 100 C 240 200, 540 290, 840 350 C 1100 400, 1290 440, 1400 460"
                strokeWidth={1.8}
              />
              {/* offshoot leaves — short curving lines branching
                  off the main arcs toward the bottom-right */}
              <Path d="M220 130 C 230 160, 240 185, 252 210" strokeWidth={1.2} />
              <Path d="M460 215 C 472 245, 482 270, 494 295" strokeWidth={1.2} />
              <Path d="M700 282 C 712 312, 722 337, 734 362" strokeWidth={1.2} />
              <Path d="M940 340 C 952 370, 962 395, 974 420" strokeWidth={1.2} />
              {/* leaves curving up-left for variety */}
              <Path d="M340 180 C 322 150, 312 130, 304 110" strokeWidth={1.1} />
              <Path d="M580 250 C 562 220, 552 200, 544 180" strokeWidth={1.1} />
              <Path d="M820 308 C 802 278, 792 258, 784 238" strokeWidth={1.1} />
            </G>
          </Svg>

          {/* Bottom-right vine: enters from off-screen bottom-right,
              arcs up and to the left, terminates partway across.
              Frames the seam from below, balancing the top vine. */}
          <Svg
            width={900}
            height={380}
            viewBox="0 0 900 380"
            style={{ position: "absolute", bottom: -40, right: -80, opacity: accentOpacity }}
          >
            <G stroke={stroke} strokeLinecap="round" fill="none">
              <Path
                d="M900 380 C 700 340, 480 290, 280 240 C 140 205, 40 175, -20 160"
                strokeWidth={2.2}
              />
              <Path
                d="M900 320 C 720 295, 510 255, 320 215 C 180 188, 70 170, -10 165"
                strokeWidth={1.7}
              />
              <Path d="M660 322 C 650 295, 642 275, 632 252" strokeWidth={1.1} />
              <Path d="M440 272 C 430 245, 422 225, 412 202" strokeWidth={1.1} />
              <Path d="M220 222 C 210 195, 202 175, 192 152" strokeWidth={1.1} />
            </G>
          </Svg>
        </>
      ) : (
        <>
          {/* Top-right vine on green: enters from off-screen top-right,
              arcs down-left, exits left edge mid-section. Continues
              the diagonal motion established in the beige section
              but in the opposite direction (so the two sections
              read as related, not duplicated). */}
          <Svg
            width={1400}
            height={520}
            viewBox="0 0 1400 520"
            style={{
              position: "absolute",
              top: -40,
              right: -120,
              opacity: primaryOpacity,
              transform: [{ scaleX: -1 }] as unknown as never,
            }}
          >
            <G stroke={stroke} strokeLinecap="round" fill="none">
              <Path
                d="M-40 40 C 220 130, 520 230, 820 300 C 1080 360, 1280 410, 1400 440"
                strokeWidth={2.4}
              />
              <Path
                d="M-30 100 C 240 200, 540 290, 840 350 C 1100 400, 1290 440, 1400 460"
                strokeWidth={1.8}
              />
              <Path d="M220 130 C 230 160, 240 185, 252 210" strokeWidth={1.2} />
              <Path d="M460 215 C 472 245, 482 270, 494 295" strokeWidth={1.2} />
              <Path d="M700 282 C 712 312, 722 337, 734 362" strokeWidth={1.2} />
              <Path d="M940 340 C 952 370, 962 395, 974 420" strokeWidth={1.2} />
              <Path d="M340 180 C 322 150, 312 130, 304 110" strokeWidth={1.1} />
              <Path d="M580 250 C 562 220, 552 200, 544 180" strokeWidth={1.1} />
              <Path d="M820 308 C 802 278, 792 258, 784 238" strokeWidth={1.1} />
            </G>
          </Svg>

          {/* Bottom-left vine on green: enters from off-screen
              bottom-left, arcs up-right, terminates partway across.
              Mirror of the beige's bottom-right vine. */}
          <Svg
            width={900}
            height={380}
            viewBox="0 0 900 380"
            style={{
              position: "absolute",
              bottom: -80,
              left: -80,
              opacity: accentOpacity,
              transform: [{ scaleX: -1 }] as unknown as never,
            }}
          >
            <G stroke={stroke} strokeLinecap="round" fill="none">
              <Path
                d="M900 380 C 700 340, 480 290, 280 240 C 140 205, 40 175, -20 160"
                strokeWidth={2.2}
              />
              <Path
                d="M900 320 C 720 295, 510 255, 320 215 C 180 188, 70 170, -10 165"
                strokeWidth={1.7}
              />
              <Path d="M660 322 C 650 295, 642 275, 632 252" strokeWidth={1.1} />
              <Path d="M440 272 C 430 245, 422 225, 412 202" strokeWidth={1.1} />
              <Path d="M220 222 C 210 195, 202 175, 192 152" strokeWidth={1.1} />
            </G>
          </Svg>
        </>
      )}
    </View>
  );
}
